import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar, Clock, BookOpen, Quote, ArrowUpRight } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { BLOG_CATEGORIES, BLOG_TEAM, BLOG_TESTIMONIALS, BLOG_POSTS } from "@/lib/blogData";
import BrandMonogramEdge from "@/components/BrandMonogramEdge";
import PageHero from "@/components/PageHero";
import PageStickyNav from "@/components/PageStickyNav";

const pick = (obj, lang) => {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj[lang] || obj.en || obj.fr || "";
};

const formatDate = (iso, lang) => {
  try {
    const d = new Date(iso);
    const locales = { en: "en-GB", fr: "fr-FR", es: "es-ES" };
    return d.toLocaleDateString(locales[lang] || "en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

// Dark Academia palette — used across the blog
const ESPRESSO = "#2C1B12";
const GOLD = "#D4AF37";
const PARCHMENT = "#E9DDC7";
const FOG = "#A89B85";

const CategoryCard = ({ cat, lang, idx }) => (
  <Link
    to={cat.to}
    data-testid={`blog-category-${cat.key}`}
    data-cms-scope={`blog-category.${cat.key}`}
    className="group relative aspect-[4/5] overflow-hidden block"
    style={{ backgroundColor: ESPRESSO }}
  >
    <img
      src={cat.image}
      alt={pick(cat.label, lang)}
      className="absolute inset-0 w-full h-full object-cover opacity-70 grayscale group-hover:grayscale-0 group-hover:opacity-90 group-hover:scale-105 transition-all duration-[900ms]" loading="lazy" decoding="async"
      data-cms-role="card.image"/>
    <div
      className="absolute inset-0"
      style={{
        background: `linear-gradient(180deg, ${ESPRESSO}33 0%, ${ESPRESSO}aa 60%, ${ESPRESSO}f0 100%)`,
      }}
    />
    <div className="absolute inset-0 p-6 md:p-8 flex flex-col text-[#F7EFE0]">
      <span
        className="text-[10px] tracking-[0.4em] uppercase"
        style={{ color: GOLD }}
      >
        {String(idx + 1).padStart(2, "0")} · {pick({ en: "Series", fr: "Série", es: "Serie" }, lang)}
      </span>
      <div className="mt-auto">
        <h3
          className="font-serif-x text-3xl md:text-4xl tracking-tight leading-tight"
          style={{ color: "#F7EFE0" }}
        >
          {pick(cat.label, lang)}
        </h3>
        <p className="mt-3 text-sm leading-relaxed" style={{ color: PARCHMENT }}>
          {pick(cat.blurb, lang)}
        </p>
        <span
          className="mt-5 inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase pb-1 w-fit border-b transition-colors"
          style={{ borderColor: `${GOLD}55`, color: GOLD }}
        >
          {pick({ en: "Read the series", fr: "Lire la série", es: "Leer la serie" }, lang)}
          <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={1.5} />
        </span>
      </div>
    </div>
  </Link>
);

const PostCard = ({ post, lang, size = "default" }) => {
  const cat = BLOG_CATEGORIES.find((c) => c.key === post.category);
  const catLabel = cat ? pick(cat.label, lang) : post.category;
  const isLarge = size === "large";
  return (
    <article
      data-testid={`blog-post-${post.slug}`}
      data-cms-scope={`blog-post.${post.slug}`}
      className="group block"
    >
      <div className={`relative overflow-hidden ${isLarge ? "aspect-[16/10]" : "aspect-[4/3]"}`} style={{ backgroundColor: ESPRESSO }}>
        <img
          src={post.featuredImage}
          alt={pick(post.title, lang)}
          className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:scale-[1.04] transition-transform duration-[900ms]" loading="lazy" decoding="async"
          data-cms-role="featured-image"/>
        <div
          className="absolute inset-0 opacity-30 group-hover:opacity-10 transition-opacity duration-700"
          style={{ background: `linear-gradient(180deg, transparent 40%, ${ESPRESSO} 100%)` }}
        />
        <span
          className="absolute top-4 left-4 inline-flex items-center gap-1.5 text-[10px] tracking-[0.3em] uppercase px-2.5 py-1.5"
          style={{ backgroundColor: `${ESPRESSO}e6`, color: GOLD, border: `1px solid ${GOLD}40` }}
        >
          {catLabel}
        </span>
      </div>
      <div className="mt-5">
        <div className="flex items-center gap-3 text-[11px] tracking-[0.2em] uppercase" style={{ color: FOG }}>
          <span className="inline-flex items-center gap-1.5">
            <Calendar className="w-3 h-3" strokeWidth={1.5} />
            {formatDate(post.publishedAt, lang)}
          </span>
          <span className="opacity-60">·</span>
          <span className="inline-flex items-center gap-1.5">
            <Clock className="w-3 h-3" strokeWidth={1.5} />
            {post.readingTime} {pick({ en: "min", fr: "min", es: "min" }, lang)}
          </span>
        </div>
        <h3
          className={`font-serif-x mt-3 leading-snug tracking-tight transition-colors duration-300 ${
            isLarge ? "text-3xl md:text-4xl" : "text-xl md:text-2xl"
          }`}
          style={{ color: "#F7EFE0" }}
        >
          <span className="group-hover:text-[#D4AF37] transition-colors">{pick(post.title, lang)}</span>
        </h3>
        <p className={`mt-3 leading-relaxed ${isLarge ? "text-base md:text-lg" : "text-sm"}`} style={{ color: PARCHMENT }}>
          {pick(post.excerpt, lang)}
        </p>
        <div className="mt-4 flex items-center justify-between">
          <span className="text-xs tracking-[0.15em] uppercase" style={{ color: FOG }}>
            {post.author.name} · {pick(post.author.role, lang)}
          </span>
          <span
            className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.3em] uppercase"
            style={{ color: GOLD }}
          >
            {pick({ en: "Read", fr: "Lire", es: "Leer" }, lang)}
            <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" strokeWidth={1.5} />
          </span>
        </div>
      </div>
    </article>
  );
};

const TeamCard = ({ member, lang }) => (
  <article
    data-testid={`blog-team-${member.id}`}
    data-cms-scope={`blog-team.${member.id}`}
    className="break-inside-avoid mb-6 border p-6 md:p-7"
    style={{ borderColor: `${GOLD}30`, backgroundColor: `${ESPRESSO}cc` }}
  >
    <div className="flex items-start gap-4">
      <img
        src={member.photo}
        alt={member.name}
        className="w-14 h-14 rounded-full object-cover grayscale"
        style={{ border: `1px solid ${GOLD}66` }} loading="lazy" decoding="async"
        data-cms-role="avatar"/>
      <div className="flex-1">
        <h4 className="font-serif-x text-xl md:text-2xl" style={{ color: "#F7EFE0" }}>{member.name}</h4>
        <p className="text-[10px] tracking-[0.3em] uppercase mt-1" style={{ color: GOLD }}>
          {pick(member.role, lang)}
        </p>
      </div>
    </div>
    <Quote className="w-5 h-5 mt-5" strokeWidth={1.2} style={{ color: GOLD }} />
    <blockquote className="font-serif-x italic mt-3 text-lg leading-snug" style={{ color: PARCHMENT }}>
      “{pick(member.quote, lang)}”
    </blockquote>
  </article>
);

const TestimonialCard = ({ t, lang }) => (
  <article
    data-testid={`blog-testimonial-${t.id}`}
    className="break-inside-avoid mb-6 p-6 md:p-7"
    style={{ backgroundColor: `${PARCHMENT}10`, border: `1px solid ${PARCHMENT}25` }}
  >
    <Quote className="w-5 h-5" strokeWidth={1.2} style={{ color: GOLD }} />
    <blockquote className="font-serif-x italic mt-3 text-base md:text-lg leading-snug" style={{ color: "#F7EFE0" }}>
      “{pick(t.quote, lang)}”
    </blockquote>
    <div className="mt-5 pt-4 flex flex-wrap items-baseline justify-between gap-2" style={{ borderTop: `1px solid ${GOLD}25` }}>
      <span className="text-sm font-medium" style={{ color: GOLD }}>{t.author}</span>
      <span className="text-[10px] tracking-[0.25em] uppercase" style={{ color: FOG }}>
        {pick(t.origin, lang)} · {pick(t.stay, lang)}
      </span>
    </div>
  </article>
);

const BlogPage = () => {
  const { lang } = useLanguage();
  const [activeCategory, setActiveCategory] = useState("all");

  const sortedPosts = useMemo(
    () => [...BLOG_POSTS].sort((a, b) => (a.publishedAt < b.publishedAt ? 1 : -1)),
    []
  );
  const visiblePosts = useMemo(
    () =>
      activeCategory === "all"
        ? sortedPosts
        : sortedPosts.filter((p) => p.category === activeCategory),
    [activeCategory, sortedPosts]
  );

  const filterTabs = [
    { key: "all", label: { en: "All", fr: "Tous", es: "Todos" } },
    ...BLOG_CATEGORIES.map((c) => ({ key: c.key, label: c.label })),
    { key: "people", label: { en: "People", fr: "Personnes", es: "Personas" } },
  ];

  return (
    <div
      data-testid="page-blog"
      className="min-h-screen"
      style={{ backgroundColor: ESPRESSO, color: "#F7EFE0" }}
    >
      {/* HERO — multi-slide (video first, then dark-academia photography) */}
      <section data-testid="blog-hero">
        <PageHero
          images={[
            "https://images.unsplash.com/photo-1517825738774-7de9363ef735?auto=format&fit=crop&w=2600&q=85",
            "https://images.unsplash.com/photo-1473580044384-7ba9967e16a0?auto=format&fit=crop&w=2600&q=85",
            "https://customer-assets.emergentagent.com/job_xaluca-luxury/artifacts/4p3qhcbe_Kasbah%20Hotel%20Xaluca%20-%20Ae%CC%81reas%20-%20HamidBabni-3.jpg",
          ]}
          alt="Journal · Grup Xaluca"
          scope="page.blog"
          overline={pick({ en: "Journal · Est. 1995", fr: "Journal · depuis 1995", es: "Diario · desde 1995" }, lang)}
          title={pick({
            en: "Field notes from southern Morocco.",
            fr: "Carnets de terrain du sud marocain.",
            es: "Cuadernos de campo del sur de Marruecos.",
          }, lang)}
          sub={pick({
            en: "Long-form essays, interviews and dispatches from our houses, our family and the wider south — written by the people who live and work there.",
            fr: "Essais, entretiens et dépêches au long cours depuis nos adresses, notre famille et le grand sud — écrits par celles et ceux qui y vivent et y travaillent.",
            es: "Ensayos, entrevistas y crónicas extensas desde nuestras casas, nuestra familia y el gran sur — escritos por quienes viven y trabajan allí.",
          }, lang)}
        />
      </section>

      <PageStickyNav
        items={[
          { id: "blog-categories", label: { en: "Series", fr: "Séries", es: "Series" } },
          { id: "blog-human", label: { en: "Voices", fr: "Voix", es: "Voces" } },
          { id: "blog-feed", label: { en: "Articles", fr: "Articles", es: "Artículos" } },
          { id: "blog-closing", label: { en: "Contact", fr: "Contact", es: "Contacto" } },
        ]}
      />

      {/* EXPERIENCE CATEGORIES */}
      <section id="blog-categories" className="py-20 md:py-28 scroll-mt-32" data-testid="blog-categories">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-8 mb-12 items-end">
            <div className="md:col-span-7">
              <p className="text-[10px] tracking-[0.4em] uppercase" style={{ color: GOLD }}>
                {pick({ en: "Experience series", fr: "Séries d'expérience", es: "Series de experiencia" }, lang)}
              </p>
              <h2 className="font-serif-x mt-3 text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]" style={{ color: "#F7EFE0" }}>
                {pick({
                  en: "Four chapters, one south.",
                  fr: "Quatre chapitres, un même sud.",
                  es: "Cuatro capítulos, un mismo sur.",
                }, lang)}
              </h2>
            </div>
            <div className="md:col-span-5">
              <p className="text-base leading-relaxed" style={{ color: PARCHMENT }}>
                {pick({
                  en: "Our journal is organised around the four ways we host: the country, the houses, the events we stage, and the camps we pitch in the dunes.",
                  fr: "Notre journal s'organise autour des quatre façons dont nous accueillons : le pays, les maisons, les événements que nous orchestrons, et les camps que nous plantons dans les dunes.",
                  es: "Nuestro diario se organiza en torno a las cuatro formas en que recibimos: el país, las casas, los eventos que organizamos y los campamentos que montamos en las dunas.",
                }, lang)}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {BLOG_CATEGORIES.map((cat, idx) => (
              <CategoryCard key={cat.key} cat={cat} lang={lang} idx={idx} />
            ))}
          </div>
        </div>
      </section>

      {/* HUMAN ELEMENT — Team + Testimonials masonry */}
      <section
        id="blog-human"
        className="py-20 md:py-28 border-t border-b scroll-mt-32"
        style={{ borderColor: `${GOLD}22`, backgroundColor: `${ESPRESSO}` }}
        data-testid="blog-human"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-8 mb-12 items-end">
            <div className="md:col-span-7">
              <p className="text-[10px] tracking-[0.4em] uppercase" style={{ color: GOLD }}>
                {pick({ en: "The human element", fr: "L'élément humain", es: "El elemento humano" }, lang)}
              </p>
              <h2 className="font-serif-x mt-3 text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]" style={{ color: "#F7EFE0" }}>
                {pick({
                  en: "The voices behind the houses.",
                  fr: "Les voix derrière les maisons.",
                  es: "Las voces detrás de las casas.",
                }, lang)}
              </h2>
            </div>
            <div className="md:col-span-5">
              <p className="text-base leading-relaxed" style={{ color: PARCHMENT }}>
                {pick({
                  en: "Co-founders, directors, hosts and the guests who came back to write us — a single column of voices that, together, makes up Grup Xaluca.",
                  fr: "Co-fondateurs, directeurs, hôtes et voyageurs revenus nous écrire — une seule colonne de voix qui, ensemble, fait Grup Xaluca.",
                  es: "Cofundadores, directores, anfitriones y huéspedes que volvieron a escribirnos — una sola columna de voces que, juntas, forman Grup Xaluca.",
                }, lang)}
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 lg:gap-10">
            {/* TEAM column */}
            <div>
              <p className="text-[10px] tracking-[0.4em] uppercase mb-5" style={{ color: GOLD }}>
                {pick({ en: "Team", fr: "Équipe", es: "Equipo" }, lang)}
              </p>
              <div className="columns-1 sm:columns-2 gap-5">
                {BLOG_TEAM.map((m) => (
                  <TeamCard key={m.id} member={m} lang={lang} />
                ))}
              </div>
            </div>
            {/* TESTIMONIALS column */}
            <div>
              <p className="text-[10px] tracking-[0.4em] uppercase mb-5" style={{ color: GOLD }}>
                {pick({ en: "Guests", fr: "Voyageurs", es: "Huéspedes" }, lang)}
              </p>
              <div className="columns-1 sm:columns-2 gap-5">
                {BLOG_TESTIMONIALS.map((t) => (
                  <TestimonialCard key={t.id} t={t} lang={lang} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ARTICLE FEED */}
      <section id="blog-feed" className="py-20 md:py-28 scroll-mt-32" data-testid="blog-feed">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-8 mb-12 items-end">
            <div className="md:col-span-7">
              <p className="text-[10px] tracking-[0.4em] uppercase" style={{ color: GOLD }}>
                {pick({ en: "Latest dispatches", fr: "Dernières dépêches", es: "Últimas crónicas" }, lang)}
              </p>
              <h2 className="font-serif-x mt-3 text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]" style={{ color: "#F7EFE0" }}>
                {pick({
                  en: "Recent essays & interviews.",
                  fr: "Essais & entretiens récents.",
                  es: "Ensayos y entrevistas recientes.",
                }, lang)}
              </h2>
            </div>
            <div className="md:col-span-5 md:flex md:justify-end">
              <div className="flex flex-wrap gap-2" data-testid="blog-filter-tabs">
                {filterTabs.map((t) => {
                  const active = activeCategory === t.key;
                  return (
                    <button
                      key={t.key}
                      type="button"
                      onClick={() => setActiveCategory(t.key)}
                      className="px-3.5 py-2 text-[10px] tracking-[0.3em] uppercase transition-colors"
                      style={{
                        border: `1px solid ${active ? GOLD : `${GOLD}33`}`,
                        backgroundColor: active ? GOLD : "transparent",
                        color: active ? ESPRESSO : PARCHMENT,
                      }}
                      data-testid={`blog-filter-${t.key}`}
                    >
                      {pick(t.label, lang)}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Featured + grid layout */}
          {visiblePosts.length > 0 ? (
            <>
              <div className="grid md:grid-cols-12 gap-8 lg:gap-10 mb-12">
                <div className="md:col-span-7">
                  <PostCard post={visiblePosts[0]} lang={lang} size="large" />
                </div>
                <div className="md:col-span-5 grid grid-cols-1 gap-8">
                  {visiblePosts.slice(1, 3).map((p) => (
                    <PostCard key={p.id} post={p} lang={lang} />
                  ))}
                </div>
              </div>
              {visiblePosts.length > 3 && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10 pt-10 border-t" style={{ borderColor: `${GOLD}22` }}>
                  {visiblePosts.slice(3).map((p) => (
                    <PostCard key={p.id} post={p} lang={lang} />
                  ))}
                </div>
              )}
            </>
          ) : (
            <p className="text-center py-16 text-base" style={{ color: PARCHMENT }} data-testid="blog-empty">
              {pick({
                en: "No essays in this category yet — pour a tea, come back next week.",
                fr: "Pas encore d'essai dans cette catégorie — versez-vous un thé, revenez la semaine prochaine.",
                es: "Aún no hay ensayos en esta categoría — sírvase un té y vuelva la semana próxima.",
              }, lang)}
            </p>
          )}
        </div>
      </section>

      {/* CLOSING — subscribe / contact band */}
      <section
        id="blog-closing"
        className="py-20 md:py-28 border-t scroll-mt-32"
        style={{ borderColor: `${GOLD}33`, backgroundColor: `${ESPRESSO}` }}
        data-testid="blog-closing"
      >
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <p className="text-[10px] tracking-[0.4em] uppercase" style={{ color: GOLD }}>
            {pick({ en: "Closing note", fr: "En guise de clôture", es: "Nota de cierre" }, lang)}
          </p>
          <h2
            className="font-serif-x mt-4 text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.1]"
            style={{ color: "#F7EFE0" }}
          >
            {pick({
              en: "Want a copy delivered with your tea?",
              fr: "Envie d'un exemplaire livré avec votre thé ?",
              es: "¿Quiere un ejemplar entregado con su té?",
            }, lang)}
          </h2>
          <p
            className="mt-6 max-w-2xl mx-auto text-base md:text-lg leading-relaxed"
            style={{ color: PARCHMENT }}
          >
            {pick({
              en: "We send a single, considered essay every two weeks — never a newsletter, never a promotion. Write to the concierge if you would like to be added.",
              fr: "Nous envoyons un seul essai soigné toutes les deux semaines — jamais de newsletter, jamais de promotion. Écrivez à notre conciergerie pour être ajouté à la liste.",
              es: "Enviamos un único ensayo cuidado cada dos semanas — nunca un boletín, nunca una promoción. Escriba al conserje si desea ser añadido.",
            }, lang)}
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/contact"
              className="inline-flex items-center gap-3 px-8 py-4 text-xs tracking-[0.3em] uppercase transition-colors"
              style={{ backgroundColor: GOLD, color: ESPRESSO }}
              data-testid="blog-cta-contact"
            >
              {pick({ en: "Contact the editor", fr: "Écrire à la rédaction", es: "Escribir a la redacción" }, lang)}
              <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
            </Link>
            <Link
              to="/press"
              className="inline-flex items-center gap-2 text-xs tracking-[0.3em] uppercase pb-1 border-b transition-colors"
              style={{ color: PARCHMENT, borderColor: `${GOLD}55` }}
              data-testid="blog-cta-press"
            >
              {pick({ en: "Press & media", fr: "Presse & médias", es: "Prensa y medios" }, lang)}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPage;
